// A slime's room as data: how big it is, what is on the walls and floor, and where each piece of furniture stands.
// A student rearranges their own room (decorating, in lobby.ts) and the layout is saved with the slime like its look,
// so the rules are the look's: ids are permanent, add new ones, never rename or reuse. No three.js here, so the game
// server checks a saved layout (cleanLayout) with the same catalog.
//
// The floor is a grid of square cells (half a tatami wide). Rows count from the back wall (row 0) to the open front;
// columns from the left. A piece stands on the cells its footprint covers; a wall piece hangs on a wall at a distance
// along it, in cells. A bigger room is just more cells, so a layout made in a small room still fits a larger one.

export const CELL = 0.92; // one floor cell, in game units

export type RoomSizeId = 'small' | 'medium' | 'large';
export const ROOM_SIZES: Record<RoomSizeId, { cols: number; rows: number }> = {
  small: { cols: 10, rows: 8 }, // (8 by 6 till Oct 29: Spencer wanted more room to move the starter things round; a
  medium: { cols: 12, rows: 9 }, // room is its own place behind the door, so it needn't fit the house it's in)
  large: { cols: 14, rows: 10 },
};

// wallpapers and floors: each id is a look in nagaya.ts (the shop's textures, some tinted; GPT art can be added as new
// ids). The names are what the closet shows.
export const WALLS = ['plaster', 'wood', 'plaster-sakura', 'plaster-matcha', 'plaster-sora'] as const;
export const FLOORS = ['tatami', 'wood_floor', 'tatami-aoi'] as const;
export const FINISH_NAMES: Record<string, [string, string]> = {
  plaster: ['Plaster', 'しっくい'],
  wood: ['Wood boards', '板張り'],
  'plaster-sakura': ['Sakura', 'さくら色'],
  'plaster-matcha': ['Matcha', '抹茶色'],
  'plaster-sora': ['Sky', '空色'],
  tatami: ['Tatami', 'たたみ'],
  wood_floor: ['Wood floor', 'フローリング'],
  'tatami-aoi': ['New tatami', '青だたみ'],
};

export type Wall = 'back' | 'left' | 'right';
export interface Furniture {
  foot: [number, number]; // cells across and deep, facing the front
  blocks: boolean; // slimes can't hop through it
  wall?: true; // hangs on a wall instead of standing on the floor
  use?: RoomUse; // something a slime does there (stand in front and press E, or tap its button)
  fixed?: true; // built in: it never moves (the closet)
  keep?: true; // can be moved but never put away, because the room needs it (a TV: either one will do)
  name: [string, string]; // English and Japanese, for the buttons
}
// the TV plays solo games; at home, the school bag opens the class board and the calendar its schedule; the sign
// by the door says who may come in; and the closet (押入れ) is where rearranging starts and where pieces not in use
// are kept; the プリ帳 holds the slime's purikura prints
export type RoomUse = 'tv' | 'bag' | 'calendar' | 'sign' | 'closet' | 'album';
export const FURNITURE: Record<string, Furniture> = {
  tv: { foot: [2, 1], blocks: true, use: 'tv', keep: true, name: ['Wood TV', '木のテレビ'] },
  console: { foot: [1, 1], blocks: true, name: ['80s console', '80年代のゲーム機'] },
  cushion: { foot: [1, 1], blocks: false, name: ['Cushion', 'ざぶとん'] },
  'low-table': { foot: [2, 2], blocks: true, name: ['Low table', 'ちゃぶ台'] },
  shelf: { foot: [1, 1], blocks: true, name: ['Shelf', 'たな'] },
  'school-bag': { foot: [1, 1], blocks: true, use: 'bag', name: ['School bag', 'かばん'] },
  window: { foot: [2, 1], blocks: false, wall: true, name: ['Window', 'まど'] },
  calendar: { foot: [1, 1], blocks: false, wall: true, use: 'calendar', name: ['Calendar', 'カレンダー'] },
  'door-sign': { foot: [1, 1], blocks: false, wall: true, use: 'sign', keep: true, name: ['Door sign', 'かけふだ'] },
  closet: { foot: [2, 1], blocks: false, wall: true, use: 'closet', fixed: true, name: ['Closet', '押入れ'] },
  // the 1990s TV and console, for a student who likes them better (either TV will do: the room needs one)
  'tv-90s': { foot: [2, 1], blocks: true, use: 'tv', keep: true, name: ['90s TV', '90年代のテレビ'] },
  'console-90s': { foot: [1, 1], blocks: true, name: ['90s console', '90年代のゲーム機'] },
  // from the capsules (R049)
  andon: { foot: [1, 1], blocks: true, name: ['Paper lantern', 'あんどん'] },
  bonsai: { foot: [1, 1], blocks: true, name: ['Bonsai', 'ぼんさい'] },
  kotatsu: { foot: [2, 2], blocks: true, name: ['Kotatsu', 'こたつ'] },
  tansu: { foot: [2, 1], blocks: true, name: ['Chest of drawers', 'たんす'] },
  bookcase: { foot: [1, 1], blocks: true, name: ['Bookcase', '本だな'] },
  goldfish: { foot: [1, 1], blocks: true, name: ['Goldfish bowl', '金魚ばち'] },
  kakejiku: { foot: [1, 1], blocks: false, wall: true, name: ['Hanging scroll', 'かけじく'] },
  'hop-poster': { foot: [1, 1], blocks: false, wall: true, name: ['Slime Hop poster', 'ポスター'] },
  // from a season's capsules
  'pumpkin-lamp': { foot: [1, 1], blocks: true, name: ['Pumpkin lamp', 'かぼちゃランプ'] },
  kadomatsu: { foot: [1, 1], blocks: true, name: ['Kadomatsu', '門松'] },
  // earned by learning, never pulled or bought
  'meteor-trophy': { foot: [1, 1], blocks: true, name: ['Meteor trophy', 'トロフィー'] },
  'fixit-certificate': { foot: [1, 1], blocks: false, wall: true, name: ['Certificate', '賞状'] },
  daruma: { foot: [1, 1], blocks: true, name: ['Daruma', 'だるま'] },
  // won in the cellar's second hall, at the cranes and the 一番くじ (prizes.ts), never pulled or bought
  'plush-poppo': { foot: [1, 1], blocks: false, name: ['Pigeon plush', 'ハトのぬいぐるみ'] },
  'plush-namigon': { foot: [1, 1], blocks: false, name: ['Namigon plush', 'ナミゴンのぬいぐるみ'] },
  'plush-nemuneko': { foot: [1, 1], blocks: false, name: ['Nemuneko plush', 'ねむねこのぬいぐるみ'] },
  'plush-konkon': { foot: [1, 1], blocks: false, name: ['Konkon plush', 'こんこんのぬいぐるみ'] },
  'plush-ponpoko': { foot: [1, 1], blocks: false, name: ['Ponpoko plush', 'ぽんぽこのぬいぐるみ'] },
  penlight: { foot: [1, 1], blocks: false, name: ['Penlight', 'ペンライト'] },
  'big-nemuneko': { foot: [1, 1], blocks: true, name: ['Big Nemuneko', 'おおきなねむねこ'] },
  'daiboshi-figure': { foot: [1, 1], blocks: true, name: ['Daiboshi figure', 'ダイボシのフィギュア'] },
  'tour-towel': { foot: [1, 1], blocks: false, wall: true, name: ['Tour towel', 'ツアータオル'] },
  'daiboshi-big': { foot: [1, 1], blocks: true, name: ['Big Daiboshi', 'でっかいダイボシ'] },
  // from the purikura booth (PRINT_PIECES): the album that keeps a slime's prints, and a print pinned up on a wall,
  // half a cell wide so a row of them can go side by side
  'puri-album': { foot: [1, 1], blocks: false, use: 'album', keep: true, name: ['Purikura album', 'プリ帳'] },
  print: { foot: [0.5, 1], blocks: false, wall: true, name: ['Purikura print', 'プリ'] },
};
// Pieces that come from the booth, never from the capsules or a gift: the album once a slime has printed, and one
// print per saved sheet (ownedFurniture's `prints`)
export const PRINT_PIECES = ['puri-album', 'print'];
export const PRINTS_MAX = 24; // whole sheets a slime keeps, newest last
const PRINT_ID = /^[0-9a-f]{24}$/;

export const ITEMS_MAX = 64; // pieces in one room

// What every slime has to furnish its room with: one of each of these. Pieces earned later (capsules, learning) are
// the slime's own list on the server (`extra`, cleanFurniture), added to these, never taken from them.
const STARTER_IDS = ['tv', 'tv-90s', 'console', 'console-90s', 'cushion', 'low-table', 'shelf', 'school-bag', 'window', 'calendar', 'door-sign', 'closet'];
export const STARTER: Record<string, number> = Object.fromEntries(STARTER_IDS.map((id) => [id, 1]));
// the pieces a slime can earn, and a slime's earned list cleaned (known ids, once each)
export const EARNED_FURNITURE = Object.keys(FURNITURE).filter((id) => !STARTER[id] && !PRINT_PIECES.includes(id));
export const cleanFurniture = (v: unknown): string[] =>
  Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && EARNED_FURNITURE.includes(x)))] : [];
// everything a slime has: the starter pieces, what it has earned, and from the booth its album and as many prints as
// it has kept
export function ownedFurniture(extra: unknown, prints = 0): Record<string, number> {
  const owned = { ...STARTER };
  for (const id of cleanFurniture(extra)) owned[id] = (owned[id] ?? 0) + 1;
  if (prints > 0) Object.assign(owned, { 'puri-album': 1, print: Math.min(prints, PRINTS_MAX) });
  return owned;
}
// as many of every piece as a room can hold: for drawing a room the server has already checked against its owner's
export const ANY_FURNITURE: Record<string, number> = Object.fromEntries(Object.keys(FURNITURE).map((id) => [id, ITEMS_MAX]));

export interface Placed {
  id: string; // a FURNITURE id
  col: number; // floor pieces: the footprint's left-back cell; wall pieces: the middle, in cells along the wall
  row: number; // floor pieces only (0 at the back wall)
  face?: 0 | 1 | 2 | 3; // quarter turns; a turned piece's footprint swaps across and deep
  wall?: Wall;
  print?: string; // a pinned print: which of the slime's sheets (its name in the sticker store)
}
export interface RoomLayout {
  v: 1;
  size: RoomSizeId;
  wall: string; // a WALLS id
  floor: string; // a FLOORS id
  items: Placed[];
}

export const DEFAULT_LAYOUT: RoomLayout = {
  v: 1,
  size: 'small',
  wall: 'plaster',
  floor: 'tatami',
  items: [
    { id: 'tv', col: 1, row: 0 },
    { id: 'console', col: 2, row: 1 },
    { id: 'cushion', col: 1, row: 2 },
    { id: 'low-table', col: 5, row: 3 },
    { id: 'shelf', col: 9, row: 0 },
    { id: 'school-bag', col: 9, row: 5 },
    { id: 'window', wall: 'back', col: 6, row: 0 },
    { id: 'calendar', wall: 'back', col: 3.4, row: 0 },
    { id: 'door-sign', wall: 'left', col: 4.6, row: 0 },
    { id: 'closet', wall: 'left', col: 3, row: 0 },
  ],
};

// 先生's house at the head of the lane: the large room, already lived in (the teacher has one of every piece and can
// rearrange it like a student's room, teacher.ts)
export const SENSEI_LAYOUT: RoomLayout = {
  v: 1,
  size: 'large',
  wall: 'wood',
  floor: 'tatami',
  items: [
    { id: 'tv', col: 1, row: 0 },
    { id: 'console', col: 2, row: 1 },
    { id: 'cushion', col: 1, row: 2 },
    { id: 'meteor-trophy', col: 3, row: 0 },
    { id: 'kotatsu', col: 5, row: 3 },
    { id: 'tansu', col: 7, row: 0 },
    { id: 'bookcase', col: 9, row: 0 },
    { id: 'shelf', col: 10, row: 0 },
    { id: 'bonsai', col: 11, row: 0 },
    { id: 'andon', col: 11, row: 4 },
    { id: 'goldfish', col: 9, row: 5 },
    { id: 'daruma', col: 4, row: 0 },
    { id: 'window', wall: 'back', col: 6, row: 0 },
    { id: 'kakejiku', wall: 'back', col: 9, row: 0 },
    { id: 'calendar', wall: 'back', col: 3.6, row: 0 },
    { id: 'fixit-certificate', wall: 'right', col: 2.5, row: 0 },
    { id: 'hop-poster', wall: 'right', col: 4.5, row: 0 },
    { id: 'door-sign', wall: 'left', col: 4.6, row: 0 },
    { id: 'closet', wall: 'left', col: 3, row: 0 },
  ],
};
// everything there is, one of each: what 先生 has to furnish the house with
export const SENSEI_OWNS: Record<string, number> = Object.fromEntries(Object.keys(FURNITURE).filter((id) => !PRINT_PIECES.includes(id)).map((id) => [id, 1]));

export const roomDims = (l: RoomLayout) => {
  const { cols, rows } = ROOM_SIZES[l.size] ?? ROOM_SIZES.small;
  return { cols, rows, w: cols * CELL, d: rows * CELL };
};

// a floor piece's footprint, from the room's middle: [x0, x1, z0, z1]
export function footprint(l: RoomLayout, p: Placed): [number, number, number, number] {
  const f = FURNITURE[p.id]?.foot ?? [1, 1];
  const [a, b] = (p.face ?? 0) % 2 ? [f[1], f[0]] : f;
  const { w, d } = roomDims(l);
  const x0 = p.col * CELL - w / 2;
  const z0 = p.row * CELL - d / 2;
  return [x0, x0 + a * CELL, z0, z0 + b * CELL];
}

// a piece's size along the wall (wall pieces) or across and deep (floor pieces), after its turns
const span = (p: Placed): [number, number] => {
  const f = FURNITURE[p.id]?.foot ?? [1, 1];
  return (p.face ?? 0) % 2 ? [f[1], f[0]] : f;
};
const wallLength = (l: RoomLayout, wall: Wall | undefined) => {
  const { cols, rows } = roomDims(l);
  return !wall || wall === 'back' ? cols : rows;
};

// Whether a piece fits among `others`: inside the room, the front row clear (it is the way in), and no two pieces on
// one floor cell or overlapping on a wall.
export function fits(l: RoomLayout, p: Placed, others: Placed[]): boolean {
  const f = FURNITURE[p.id];
  if (!f) return false;
  const [a, b] = span(p);
  if (f.wall) {
    const wall = p.wall ?? 'back';
    if (p.col - a / 2 < -0.001 || p.col + a / 2 > wallLength(l, wall) + 0.001) return false;
    return !others.some((q) => FURNITURE[q.id]?.wall && (q.wall ?? 'back') === wall && Math.abs(q.col - p.col) < (span(q)[0] + a) / 2 - 0.001);
  }
  const { cols, rows } = roomDims(l);
  if (p.col < 0 || p.row < 0 || p.col + a > cols || p.row + b > rows - 1) return false;
  return !others.some((q) => {
    if (!FURNITURE[q.id] || FURNITURE[q.id].wall) return false;
    const [qa, qb] = span(q);
    return p.col < q.col + qa && q.col < p.col + a && p.row < q.row + qb && q.row < p.row + b;
  });
}

// Keep only what fits: known ids, no more of a piece than the slime has (`owned`), inside the room, nothing
// overlapping. The built-in pieces stay where they are built, and a piece the room needs comes back if it went
// missing (`restore`: not while a student is carrying it across the room). A saved layout goes through this before it
// is built, so a room that shrinks or a piece that was taken out never breaks it.
export function fitLayout(l: RoomLayout, owned: Record<string, number> = STARTER, restore = true): RoomLayout {
  const size = ROOM_SIZES[l.size] ? l.size : 'small';
  const room: RoomLayout = {
    v: 1,
    size,
    wall: (WALLS as readonly string[]).includes(l.wall) ? l.wall : DEFAULT_LAYOUT.wall,
    floor: (FLOORS as readonly string[]).includes(l.floor) ? l.floor : DEFAULT_LAYOUT.floor,
    items: [],
  };
  const count = new Map<string, number>();
  const add = (p: Placed) => {
    if (room.items.length >= ITEMS_MAX || (count.get(p.id) ?? 0) >= (owned[p.id] ?? 0) || !fits(room, p, room.items)) return false;
    // a print is one sheet, pinned up once
    if (p.id === 'print' && (!p.print || room.items.some((q) => q.print === p.print))) return false;
    count.set(p.id, (count.get(p.id) ?? 0) + 1);
    room.items.push(p);
    return true;
  };
  const given = Array.isArray(l.items) ? l.items : [];
  // built-in pieces first, at their own places; then the rest in order; then anything the room needs that is missing
  for (const p of DEFAULT_LAYOUT.items) if (FURNITURE[p.id].fixed) add({ ...p });
  for (const p of given) if (FURNITURE[p.id] && !FURNITURE[p.id].fixed) add(p);
  if (restore) for (const p of DEFAULT_LAYOUT.items) if (FURNITURE[p.id].keep && !hasNeed(room, p.id) && !add({ ...p })) add(spotFor(room, p.id) ?? p);
  return room;
}

// Whether a room already has what a piece the room needs is for: the same piece, or another that does the same
// (the 1990s TV for the wood one)
function hasNeed(l: RoomLayout, id: string): boolean {
  const use = FURNITURE[id]?.use;
  return l.items.some((q) => q.id === id || (!!use && FURNITURE[q.id]?.use === use));
}
// Can a carried piece go into the closet? Not one the room needs, unless another in the room does its job.
export const canPutAway = (l: RoomLayout, id: string) => !!FURNITURE[id] && (!FURNITURE[id].keep || hasNeed(l, id));

// The first free place for a piece, from the back of the room forwards (for a piece taken out of the closet when
// there's no room where the slime stands, or one the room needs back)
export function spotFor(l: RoomLayout, id: string, wall?: Wall): Placed | null {
  const f = FURNITURE[id];
  if (!f) return null;
  const { cols, rows } = roomDims(l);
  if (f.wall) {
    for (const w of wall ? [wall] : (['back', 'left', 'right'] as Wall[]))
      for (let c = f.foot[0] / 2; c <= wallLength(l, w) - f.foot[0] / 2; c += 0.5) {
        const p: Placed = { id, wall: w, col: c, row: 0 };
        if (fits(l, p, l.items)) return p;
      }
    return null;
  }
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const p: Placed = { id, col: c, row: r };
      if (fits(l, p, l.items)) return p;
    }
  return null;
}

// Where the プリ帳 goes when a slime's first print brings it home: on the floor beside the low table (or the kotatsu),
// where a slime would sit with it; anywhere free when there's no table or no room by it
export function albumSpot(l: RoomLayout): Placed | null {
  const table = l.items.find((p) => p.id === 'low-table' || p.id === 'kotatsu');
  if (table) {
    const [a, b] = span(table);
    const by: [number, number][] = [[table.col + a, table.row], [table.col - 1, table.row], [table.col, table.row + b], [table.col + a, table.row + b - 1], [table.col, table.row - 1]];
    for (const [col, row] of by) {
      const p: Placed = { id: 'puri-album', col, row };
      if (fits(l, p, l.items)) return p;
    }
  }
  return spotFor(l, 'puri-album');
}

// The pieces a slime has that are not in its room: they wait in the closet
export function inCloset(l: RoomLayout, owned: Record<string, number> = STARTER): string[] {
  const out: string[] = [];
  for (const [id, n] of Object.entries(owned)) {
    if (!FURNITURE[id] || id === 'print') continue; // prints wait in the album, not the closet
    const used = l.items.filter((p) => p.id === id).length;
    for (let k = used; k < n; k++) out.push(id);
  }
  return out;
}

// A layout from outside (a save from a laptop, or one read back from storage), checked piece by piece: anything that
// isn't a well-formed piece is dropped, and the rest goes through fitLayout. `sizes` are the room sizes this slime
// has (only the small room so far); `prints` the sheets it has kept, the only ones it can pin up.
export function cleanLayout(raw: unknown, owned: Record<string, number> = STARTER, sizes: RoomSizeId[] = ['small'], prints: string[] = []): RoomLayout {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<RoomLayout>;
  const items: Placed[] = [];
  for (const q of Array.isArray(r.items) ? r.items.slice(0, ITEMS_MAX) : []) {
    if (!q || typeof q !== 'object' || typeof q.id !== 'string' || !FURNITURE[q.id]) continue;
    const col = Number(q.col);
    const row = Number(q.row);
    if (!Number.isFinite(col) || !Number.isFinite(row)) continue;
    if (FURNITURE[q.id].wall) {
      const wall = q.wall === 'left' || q.wall === 'right' ? q.wall : 'back';
      if (q.id === 'print' && !(typeof q.print === 'string' && PRINT_ID.test(q.print) && prints.includes(q.print))) continue;
      items.push({ id: q.id, wall, col: Math.round(col * 10) / 10, row: 0, ...(q.id === 'print' ? { print: q.print } : {}) });
    } else {
      const face = [0, 1, 2, 3].includes(Number(q.face)) ? (Number(q.face) as Placed['face']) : 0;
      items.push({ id: q.id, col: Math.round(col), row: Math.round(row), ...(face ? { face } : {}) });
    }
  }
  const size = typeof r.size === 'string' && sizes.includes(r.size as RoomSizeId) ? (r.size as RoomSizeId) : sizes[0] ?? 'small';
  return fitLayout({ v: 1, size, wall: String(r.wall ?? ''), floor: String(r.floor ?? ''), items }, owned);
}

/* ---------- who may come in ---------- */
// Each slime hangs a sign on its door: shut (the default, so nobody is surprised) or open to anyone in the yard.
// Deliberately no friends lists: in a class they become something to count, rank and take back.
export type DoorSign = 'shut' | 'open';
export const DOOR_SIGNS: DoorSign[] = ['shut', 'open'];
export const DOOR_DEFAULT: DoorSign = 'shut';
export const isDoorSign = (v: unknown): v is DoorSign => DOOR_SIGNS.includes(v as DoorSign);
export const nextSign = (s: DoorSign): DoorSign => DOOR_SIGNS[(DOOR_SIGNS.indexOf(s) + 1) % DOOR_SIGNS.length];

// Can a slime go in through door i? Its own door always, 先生's house (-1) always, and another's when its sign is open.
export function doorOpen(i: number, mine: number | null, signs: DoorSign[]): boolean {
  return i < 0 || i === mine || (signs[i] ?? DOOR_DEFAULT) === 'open';
}

// The rooms sit off to the side of the world, one every ROOM_GAP along z (nagaya.ts draws them). Each class has
// its own village and rooms, a band of LANE_BAND further along z (the game server numbers the bands), so in the
// after-school yard, which the whole school shares, every class's village is somewhere of its own.
export const ROOM_X = 520;
export const ROOM_GAP = 24;
export const LANE_MAX = 120; // doors in one class's village
export const LANE_BAND = 4000;
// which band a spot in a lane or a room is in
export const bandAt = (z: number) => Math.max(0, Math.floor((100 - z) / LANE_BAND));
// which room (in its band) a spot is in, or null when it isn't in one
// (past x 600 are the kissaten, cafe.ts, and the 商店街 it's on, shotengai.ts: not rooms)
export const roomAt = (x: number, z: number): number | null => (x > 400 && x < 600 ? Math.round((-bandAt(z) * LANE_BAND - z) / ROOM_GAP) : null);
